import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import DownloadPopup from "@/components/DownloadPopup";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { cn } from "@/lib/utils";
import HeroSection from "@/components/faq/HeroSection";

interface FAQItem {
  question: string;
  answer: string | JSX.Element;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const FAQ = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const faqCategories: FAQCategory[] = [
    {
      title: "General Questions",
      items: [
        {
          question: "What is Front Runner: Racehorse Manager?",
          answer: "Front Runner: Racehorse Manager is a free-to-play strategy game where you build, breed, train, and trade racehorses to create the most dominant racing empire. Compete against friends and other managers while earning real-world rewards."
        },
        {
          question: "How do I play Front Runner: Racehorse Manager?",
          answer: (
            <span>
              The game is currently supported on all well known <a href="https://frontrunnergame.com" target="_blank" rel="noopener noreferrer" className="text-racing-accent hover:underline">web browsers</a> including Google Chrome and Safari and is also available on <a href="https://play.google.com/store/apps/details?id=com.frontrunner.racehorse" target="_blank" rel="noopener noreferrer" className="text-racing-accent hover:underline">Google Play Store</a>.
            </span>
          )
        },
        {
          question: "When will this be available on iOS?",
          answer: "Our team is busy working on an iOS version which is scheduled to be deployed later in 2025."
        },
        {
          question: "Does the Teaser Version of the game include the entire game functionality?",
          answer: "The full game will be launched in Summer 2025. Until then, our team is working hard to continuously update the game with new features and functionality so that we can further enrich the experience."
        },
        {
          question: "Do I need prior horse racing knowledge to play?",
          answer: "No! The game is designed for everyone."
        },
        {
          question: "When is the full game released?",
          answer: "Front Runner: Racehorse Manager is scheduled for full release in Summer 2025."
        },
        {
          question: "Will the game feature unlockable content?",
          answer: "Yes! We're planning a series of new horses, cosmetics, race tracks and much more ready for full launch in Summer 2025."
        }
      ]
    },
    {
      title: "Teaser Experience vs. Full Game",
      items: [
        {
          question: "What does the teaser experience include?",
          answer: "The teaser will introduce basic navigation and a preview of the gameplay. However, the full experience offers deeper strategy, competition, and earning opportunities."
        },
        {
          question: "Who are the developers of this game?",
          answer: "The game is developed by The Winners Circle (TWC) - A Racing community brought to you by Racing League and powered by Zilliqa."
        },
        {
          question: "When does the full game launch?",
          answer: <span>
            The release schedule is as follows:<br/>
            <ul className="list-disc list-inside pl-4 mt-2 space-y-1">
              <li><b>Alpha:</b> Spring 2025</li>
              <li><b>Beta:</b> Early Summer 2025</li>
              <li><b>Full Release:</b> Late Summer 2025</li>
            </ul>
          </span>
        }
      ]
    },
    {
      title: "Full Launch Features",
      items: [
        {
          question: "How does the game work?",
          answer: "You start by managing your own stable, acquiring horses, and training them to compete in races. Earn stars whilst you level up your stable, enter tournaments, and even own racetracks to maximize your earnings."
        },
        {
          question: "What are the different ways to earn in the game?",
          answer: "With the full version of the game and upcoming updates, you'll be able to earn through racing, trading, breeding, staking, and track ownership. In the current Teaser Version you can earn through competing in races."
        },
        {
          question: "How does leveling up work?",
          answer: "As you win races and train your horses, you'll earn stars to upgrade your stable, unlock new horses, and access better competitions."
        }
      ]
    },
    {
      title: "Earning & Progression",
      items: [
        {
          question: "What is $HRSE, and how does it work in Front Runner: Racehorse Manager?",
          answer: "$HRSE is the in-game currency that powers the economy. You can earn, trade, and use it to level up your stable, enter races, and acquire top-tier horses. This will be developed further and integrated in upcoming updates."
        },
        {
          question: "How can I start earning and using $HRSE as a new player?",
          answer: "You'll begin by competing in races and completing objectives. As you progress, you can trade, breed, and invest in your stable to maximize your $HRSE earnings."
        },
        {
          question: "How does trading and leveling up with $HRSE create exciting gameplay?",
          answer: "Every win, trade, and strategic move gets you closer to building a dominant racing empire. The trading system is fast and rewarding, making every transaction feel like a step toward victory."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-racing-dark via-racing-dark to-black text-white">
      <Helmet>
        <title>Frequently Asked Questions | Front Runner: Racehorse Manager</title>
        <meta name="description" content="Find answers to common questions about Front Runner: Racehorse Manager. Learn about gameplay, rewards, progression, and upcoming features in our comprehensive FAQ." />
        <meta name="keywords" content="front runner faq, horse racing game questions, racehorse manager help, gameplay questions, $HRSE token" />
        
        <meta itemProp="name" content="Frequently Asked Questions | Front Runner: Racehorse Manager" />
        <meta itemProp="description" content="Find answers to common questions about Front Runner: Racehorse Manager. Learn about gameplay, rewards, progression, and upcoming features." />
        <meta itemProp="image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://frontrunnergame.com/faq" />
        <meta property="og:title" content="FAQ | Front Runner: Racehorse Manager" />
        <meta property="og:description" content="Find answers to common questions about gameplay, rewards, progression, and upcoming features in Front Runner: Racehorse Manager." />
        <meta property="og:image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://frontrunnergame.com/faq" />
        <meta name="twitter:title" content="FAQ | Front Runner: Racehorse Manager" />
        <meta name="twitter:description" content="Find answers to common questions about gameplay, rewards, progression, and upcoming features in Front Runner: Racehorse Manager." />
        <meta name="twitter:image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        
        <link rel="canonical" href="https://frontrunnergame.com/faq" />
      </Helmet>
      <Navigation />
      <main className="container mx-auto px-4 py-24 md:py-32">
        <div className="relative min-h-screen bg-racing-dark text-white pt-24 overflow-hidden">
          <div className="absolute inset-0" />
          <div className="absolute inset-0 bg-[url('/lovable-uploads/FRRM-website-BG.svg')] bg-cover opacity-5" />
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="container mx-auto px-4 py-12 space-y-16 relative z-10"
          >
            <HeroSection />
            
            <FAQAccordion categories={faqCategories} />

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center pt-8"
            >
              <div className="max-w-xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Ready to jump into the action?</h2>
                <DownloadPopup buttonText="Play Now" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

interface FAQAccordionProps {
  categories: FAQCategory[];
}

const FAQAccordion = ({ categories }: FAQAccordionProps) => {
  const [openItem, setOpenItem] = useState<string | null>(null);

  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItem(prevKey => prevKey === key ? null : key);
  };

  const isItemOpen = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    return openItem === key;
  };

  return (
    <div className="grid gap-10 md:gap-16 max-w-4xl mx-auto">
      {categories.map((category, categoryIndex) => (
        <motion.div
          key={category.title}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
          viewport={{ once: true }}
          className="space-y-6"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-racing-accent mb-4">
            {category.title}
          </h2>
          <div className="space-y-4">
            {category.items.map((item, itemIndex) => {
              const isOpen = isItemOpen(categoryIndex, itemIndex);
              return (
                <div 
                  key={itemIndex}
                  className={cn(
                    "border-b border-white/10 transition-all duration-300",
                    isOpen && "bg-racing-accent/5 rounded-lg border-racing-accent/20"
                  )}
                >
                  <button
                    onClick={() => toggleItem(categoryIndex, itemIndex)}
                    className={cn(
                      "w-full flex items-center justify-between py-4 text-left focus:outline-none group px-4",
                      isOpen ? "text-racing-accent" : "text-white"
                    )}
                  >
                    <span className={cn(
                      "font-medium text-lg transition-all duration-300",
                      "group-hover:text-racing-accent group-hover:translate-x-1",
                      isOpen && "text-racing-accent font-semibold"
                    )}>
                      {item.question}
                    </span>
                    <div className={cn(
                      "flex-shrink-0 ml-2 transition-all duration-300",
                      isOpen ? "text-racing-accent rotate-180" : "text-white/60",
                      "group-hover:text-racing-accent group-hover:scale-110"
                    )}>
                      <ChevronDown className="h-5 w-5" />
                    </div>
                  </button>
                  <div
                    className={cn(
                      "overflow-hidden transition-all duration-300",
                      isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    )}
                  >
                    <div className={cn(
                      "py-3 px-4 text-gray-300",
                      "bg-gradient-to-r from-racing-accent/5 to-transparent",
                      "border-l-2 border-racing-accent/20 ml-4 mb-4"
                    )}>
                      {item.answer}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default FAQ;
